import { Zip, ZipPassThrough } from "fflate";

import { signMediaUrls } from "../api";
import { supabase } from "../supabase";
import { entryPathOf, fileNameOf, mimeOf, type ExportItem } from "./archive";

/** 1回に署名を頼む件数。Worker 側の上限は120 */
const SIGN_BATCH = 100;

/** 1回の問い合わせに載せる id の数。in(...) は URL に並ぶため、長さで弾かれないよう小分けにする */
const ID_BATCH = 100;

/** 保存する全件。削除済みは含めない */
export async function fetchExportList(): Promise<ExportItem[]> {
  const { data, error } = await supabase
    .from("media")
    .select("id, storage_key, taken_at, byte_size")
    .is("deleted_at", null)
    .order("taken_at", { ascending: true });
  if (error) throw error;
  const items: ExportItem[] = data;
  return items;
}

/**
 * 選んだぶんだけの保存用リスト。
 *
 * 並びは ZIP の中と同じ撮影日時の古い順にする。選んだ順は画面を触った順で、
 * 保存したものを後から見るときの手掛かりにならない。
 */
export async function fetchExportItems(ids: readonly string[]): Promise<ExportItem[]> {
  const items: ExportItem[] = [];
  for (let start = 0; start < ids.length; start += ID_BATCH) {
    const { data, error } = await supabase
      .from("media")
      .select("id, storage_key, taken_at, byte_size")
      .in("id", ids.slice(start, start + ID_BATCH))
      .is("deleted_at", null);
    if (error) throw error;
    items.push(...(data as ExportItem[]));
  }
  return items.sort((a, b) => a.taken_at.localeCompare(b.taken_at) || a.id.localeCompare(b.id));
}

/**
 * 1件だけを原本のまま保存する。
 * ZIP に包むと展開の手間が増えるだけなので、1件のときはこちらを通す。
 */
export async function downloadOriginal(item: ExportItem): Promise<void> {
  const urls = await signMediaUrls([item.storage_key]);
  const url = urls[item.storage_key];
  if (url === undefined) throw new Error("署名URLがありません");
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`取得できませんでした (${String(response.status)})`);
  }
  saveBlob(await response.blob(), fileNameOf(item));
}

/**
 * まとめて ZIP にする。
 *
 * **圧縮はしない。** 写真も動画も既に圧縮済みで、かけ直しても縮まらないうえ、
 * 端末のCPUを何分も占有するだけになる。ZIP は1つの塊にまとめる役だけを担う。
 *
 * 出来た端から `Blob` にして手放す。ブラウザは大きな Blob をディスクに逃がすため、
 * 数GBでもメモリに載せ続けずに済む。
 */
export async function exportAsZip(
  items: readonly ExportItem[],
  onProgress: (done: number) => void,
): Promise<Blob> {
  const parts: Blob[] = [];

  const finished = new Promise<Blob>((resolve, reject) => {
    const zip = new Zip((error, chunk, final) => {
      if (error !== null) {
        reject(error);
        return;
      }
      parts.push(new Blob([chunk]));
      if (final) resolve(new Blob(parts, { type: "application/zip" }));
    });
    void appendAll(zip, items, onProgress).then(
      () => {
        zip.end();
      },
      (error: unknown) => {
        zip.terminate();
        reject(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });

  return finished;
}

async function appendAll(
  zip: Zip,
  items: readonly ExportItem[],
  onProgress: (done: number) => void,
): Promise<void> {
  let done = 0;

  for (let start = 0; start < items.length; start += SIGN_BATCH) {
    const batch = items.slice(start, start + SIGN_BATCH);
    const urls = await signMediaUrls(batch.map((item) => item.storage_key));

    // 1件ずつ入れる。同時に取りにいくと、その数だけ実体がメモリに載る
    for (const item of batch) {
      const url = urls[item.storage_key];
      if (url === undefined) continue;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`取得できませんでした (${String(response.status)})`);
      }
      const body = new Uint8Array(await response.arrayBuffer());

      const entry = new ZipPassThrough(entryPathOf(item));
      zip.add(entry);
      entry.push(body, true);

      done += 1;
      onProgress(done);
    }
  }
}

/**
 * 端末の共有シートにファイルを載せられるか。
 *
 * PC のブラウザはたいてい持っていない。空のファイルで可否だけ尋ねる
 * (`navigator.share` の有無だけでは、テキストは送れてファイルは送れない
 * ブラウザを見分けられない)。
 */
export function canShareFiles(): boolean {
  if (typeof navigator.canShare !== "function") return false;
  try {
    return navigator.canShare({ files: [new File([], "photo.jpg", { type: "image/jpeg" })] });
  } catch {
    return false;
  }
}

/**
 * 共有シートに渡す `File` を組み立てる。
 *
 * ZIP と違い**全件がメモリに載ったまま**になる(共有シートは全部揃って
 * 初めて開ける)。件数と容量は呼ぶ側で絞る(→ archive.ts の shareBlockReasonOf)。
 *
 * `signal` は取りやめ用。準備の途中でシートを閉じたら、残りを取りにいかない。
 */
export async function fetchAsFiles(
  items: readonly ExportItem[],
  onProgress: (done: number) => void,
  signal?: AbortSignal,
): Promise<File[]> {
  const files: File[] = [];

  for (let start = 0; start < items.length; start += SIGN_BATCH) {
    const batch = items.slice(start, start + SIGN_BATCH);
    const urls = await signMediaUrls(batch.map((item) => item.storage_key));

    for (const item of batch) {
      const url = urls[item.storage_key];
      if (url === undefined) continue;

      const response = await fetch(url, { signal });
      if (!response.ok) {
        throw new Error(`取得できませんでした (${String(response.status)})`);
      }
      const blob = await response.blob();
      const type = mimeOf(item.storage_key) ?? blob.type;
      files.push(new File([blob], fileNameOf(item), { type }));
      onProgress(files.length);
    }
  }

  return files;
}

/**
 * 共有シートを開く。戻り値は「保存まで進んだか」(false は取りやめ)。
 *
 * **タップと地続きで呼ぶこと。** 取得を待ってから呼ぶとブラウザに
 * 「ユーザー操作から離れた」と見なされて弾かれる。だから取得と保存は
 * 別のタップに分けてある(→ download.tsx)。
 *
 * `title` や `text` は添えない。文字が混ざると共有先が「テキストの共有」
 * として扱い、写真アプリへの保存が出ないことがある。
 */
export async function shareFiles(files: readonly File[]): Promise<boolean> {
  try {
    await navigator.share({ files: [...files] });
    return true;
  } catch (e) {
    // 共有シートを閉じただけ。失敗として扱わない
    if (e instanceof DOMException && e.name === "AbortError") return false;
    throw e;
  }
}

/** ブラウザに保存させる。同一オリジンの blob: なので download が効く */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  // 直後に破棄すると保存が始まらない端末がある
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60_000);
}
